import React, { createContext, useContext, useMemo } from "react";
import { MilestoneContextValue, MilestoneProviderProps } from "../types/milestoneComponent.types";
import { useMilestoneData } from "../hooks/useMilestoneData";
import { useMilestoneState } from "../hooks/useMilestoneState";
import { useMilestoneActions } from "../hooks/useMilestoneActions";
import MilestoneMenu from "../components/MilestoneMenu";

const MilestoneContext = createContext<MilestoneContextValue | null>(null);

// Custom hook to use milestone context with error handling
export const useMilestoneContext = () => {
     const context = useContext(MilestoneContext);
     if (!context) {
          throw new Error('useMilestoneContext must be used within a MilestoneProvider');
     }
     return context;
};

// Optimized milestone provider component
export const MilestoneProvider: React.FC<MilestoneProviderProps> = ({
     children,
     homeSiteId,
     exceptionCount,
     alertsCount,
     notesCount,
     userId,
     updateMilestoneTasks,
     saleDate,
     closeOfEscrowActual,
     closeOfEscrowEstimated,
     setMilestoneTab,
}) => {
     const { combinedMilestoneData, getTasksForMilestone, userInfo } = useMilestoneData(homeSiteId);

     const {
          selectedMilestones,
          setSelectedMilestones,
          menuAnchorEl,
          selectedMilestoneId,
          expandedAll,
          expandedAccordions,
          taskStates,
          setTaskStates,
          automatedTasks,
          isNotApplicableMilestone,
          getCompletedCounts,
          getTotalMilestones,
          toggleExpandAll,
          toggleAccordion,
          handleMenuClick,
          handleMenuClose,
          findAutomatedTasks,
     } = useMilestoneState(combinedMilestoneData, getTasksForMilestone);

     // Get divisionCode from the first task in combinedMilestoneData
     const divisionCode = combinedMilestoneData?.[0]?.tasks?.[0]?.divisionTemplate?.divisionCode || '';

     const {
          handleCheckboxChange,
          handleTaskCheckboxChange,
          handleNotApplicableMilestone,
     } = useMilestoneActions({
          combinedMilestoneData,
          getTasksForMilestone,
          findAutomatedTasks,
          selectedMilestones,
          setSelectedMilestones,
          taskStates,
          setTaskStates,
          updateMilestoneTasks,
          userId,
          homeSiteId,
          saleDate,
          closeOfEscrowActual,
          closeOfEscrowEstimated,
          selectedMilestoneId,
          handleMenuClose,
          divisionCode
     });

     // Memoize context value to prevent unnecessary re-renders
     const contextValue = useMemo(() => {
          return {
               // Data
               combinedMilestoneData,
               getTasksForMilestone,
               userInfo,

               // State
               selectedMilestones,
               taskStates,
               expandedAccordions,
               expandedAll,
               automatedTasks,
               isNotApplicableMilestone,
               getCompletedCounts,
               getTotalMilestones,

               // Actions
               handleCheckboxChange,
               handleTaskCheckboxChange,
               toggleAccordion,
               toggleExpandAll,
               handleMenuClick,

               // Props
               exceptionCount,
               alertsCount,
               notesCount,
               updateMilestoneTasks,
               saleDate,
               closeOfEscrowActual,
               closeOfEscrowEstimated,
               setMilestoneTab,
               homeSiteId,
               divisionCode,
          };
     }, [
          // Data dependencies
          combinedMilestoneData,
          userInfo,

          // State dependencies
          selectedMilestones,
          taskStates,
          expandedAccordions,
          expandedAll,
          automatedTasks,

          // Function dependencies (memoized callbacks)
          getTasksForMilestone,
          isNotApplicableMilestone,
          getCompletedCounts,
          getTotalMilestones,
          handleCheckboxChange,
          handleTaskCheckboxChange,
          toggleAccordion,
          toggleExpandAll,
          handleMenuClick,

          // Props that might change
          exceptionCount,
          alertsCount,
          notesCount,
          updateMilestoneTasks,
          saleDate,
          closeOfEscrowActual,
          closeOfEscrowEstimated,
          setMilestoneTab,
          homeSiteId,
          divisionCode,
     ]);

     return (
          <MilestoneContext.Provider value={contextValue}>
               {children}
               <MilestoneMenu
                    anchorEl={menuAnchorEl}
                    handleClose={handleMenuClose}
                    handleNotApplicableMilestone={handleNotApplicableMilestone}
                    isNotApplicableMilestone={isNotApplicableMilestone}
                    milestone={combinedMilestoneData}
                    selectedMilestoneId={selectedMilestoneId}
               />
          </MilestoneContext.Provider>
     );
}; 